// @google/genai guidelines: Firebase initialization using standard v9 modular SDK
import { initializeApp } from 'firebase/app';
import type { FirebaseApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, indexedDBLocalPersistence, browserLocalPersistence, setPersistence } from 'firebase/auth';
import type { Auth } from 'firebase/auth';
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager, getFirestore } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import type { Messaging } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: "AIzaSyCFM1MWIL6cVNZ66M5XAY1uq7dyA5tPPDA",
  authDomain: "fairshare-9ce5d.firebaseapp.com",
  projectId: "fairshare-9ce5d",
  storageBucket: "fairshare-9ce5d.firebasestorage.app",
  messagingSenderId: "340485090780",
  appId: "1:340485090780:web:4cfe5b108157d6aaecb319",
  measurementId: "G-4EY5HE2SRT"
};

const isConfigured = firebaseConfig.apiKey !== "YOUR_API_KEY_HERE";

let app: FirebaseApp | undefined;
export let auth: Auth | undefined;
export let db: Firestore | undefined;
export let googleProvider: GoogleAuthProvider | undefined;
export let isFirebaseEnabled = isConfigured;
export let messaging: Messaging | null = null;
let messagingPromise: Promise<Messaging | null> | null = null;

if (isConfigured) {
  try {
    app = initializeApp(firebaseConfig);

    auth = getAuth(app);
    if (typeof window !== 'undefined') {
      // Prioritize indexedDBLocalPersistence with fallback to browserLocalPersistence for PWA/TWA and mobile offline support
      setPersistence(auth, indexedDBLocalPersistence)
        .catch(() => setPersistence(auth!, browserLocalPersistence))
        .catch((err) => {
          console.error("❌ Auth persistence setup failed:", err);
        });

      // Modern Firestore Cache initialization (fixes deprecation and multi-tab warnings)
      try {
        db = initializeFirestore(app, {
          localCache: persistentLocalCache({
            tabManager: persistentMultipleTabManager()
          })
        });
      } catch (cacheError) {
        console.warn("⚠️ Firestore persistent cache initialization failed, falling back to default instance:", cacheError);
        db = getFirestore(app);
      }
    } else {
      db = getFirestore(app);
    }

    googleProvider = new GoogleAuthProvider();
  } catch (error) {
    console.error("Firebase initialization failed:", error);
  }
}

export const getMessagingInstance = async (): Promise<Messaging | null> => {
  if (!isConfigured || !app) return null;
  if (messaging) return messaging;
  if (messagingPromise) return messagingPromise;

  messagingPromise = (async () => {
    try {
      const { getMessaging, isSupported } = await import('firebase/messaging');
      const supported = await isSupported();
      if (supported && app) {
        messaging = getMessaging(app);
        return messaging;
      }
    } catch (e) {
      console.error("Failed to initialize Firebase Messaging:", e);
    }
    return null;
  })();

  return messagingPromise;
};

// Kick off eager initialization in background
getMessagingInstance().catch(console.error);

export const requestNotificationPermission = async (userId: string) => {
  const messagingInstance = await getMessagingInstance();
  if (!messagingInstance || !db) return false;
  try {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      const { getToken } = await import('firebase/messaging');
      const { doc, setDoc } = await import('firebase/firestore');

      let token = null;
      try {
        if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.ready;
          token = await getToken(messagingInstance, {
            vapidKey: 'BPUBAn6CQ8c5oTmXaYwl6HP2aFHDSKiPPX8_28rOpa9V-ZjCUm9dZIhunsS4vP2M_QnhCpFesArxKUBhYMU5ixs',
            serviceWorkerRegistration: registration
          });
        }
      } catch (e) {
        console.warn("FCM: service worker registration failed, falling back to default registration:", e);
      }

      if (!token) {
        token = await getToken(messagingInstance, {
          vapidKey: 'BPUBAn6CQ8c5oTmXaYwl6HP2aFHDSKiPPX8_28rOpa9V-ZjCUm9dZIhunsS4vP2M_QnhCpFesArxKUBhYMU5ixs'
        });
      }

      if (token) {
        await setDoc(doc(db, 'users', userId), { fcmToken: token }, { merge: true });
        console.log("FCM Token saved successfully");
        return true;
      }
    }
  } catch (error) {
    console.error("Failed to get push notification token:", error);
  }
  return false;
};